<template>
  <button
    type="button"
    :class="buttonClass"
    aria-haspopup="dialog"
    :aria-expanded="open ? 'true' : 'false'"
    @click="toggleModal"
  >
    {{ button }}
  </button>

  <Teleport to="body">
    <!-- OVERLAY -->
    <div v-if="open" class="acc-overlay" @click="closeModal"></div>

    <!-- DRAWER -->
    <div
      class="acc-drawer font-avenirnext text-body-sm-mobile lg:text-body-sm"
      :class="{open: open}"
      role="dialog"
      aria-modal="true"
      aria-label="Accessibility Settings"
      :aria-hidden="open ? 'false' : 'true'"
      @click.stop
    >
      <div>
        <div class="mb-30 flex items-center justify-between gap-20">
          <h2 class="acc-title font-degular text-h4-mobile font-bold uppercase">
            Accessibility Settings
          </h2>

          <!-- CLOSE BUTTON -->
          <button
            type="button"
            class="text-blue"
            aria-label="Close accessibility settings"
            @click="closeModal"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="29"
              height="29"
              viewBox="0 0 29 29"
              fill="none"
            >
              <path
                d="M21.5 7.39355L7.39356 21.5"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
              />
              <path
                d="M21.5 21.5L7.39356 7.39356"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </div>

        <div class="flex flex-wrap gap-10">
          <button
            v-for="option in options"
            :key="option.value"
            type="button"
            class="acc-option-btn"
            :class="{active: isActive(option.value)}"
            :aria-pressed="isActive(option.value) ? 'true' : 'false'"
            @click="toggleOption(option.value)"
          >
            <span class="acc-option-icon" v-html="option.icon"></span>
            {{ option.label }}
          </button>
        </div>

        <div class="mt-40 flex flex-wrap items-center gap-10">
          <button
            type="button"
            class="button !w-full sm:flex-[0_1_calc(50%-5px)]"
            @click="resetSettings"
          >
            Reset Settings
          </button>

          <a
            v-if="statementUrl"
            :href="statementUrl"
            class="button !w-full sm:flex-[0_1_calc(50%-5px)] !px-5"
          >
            Accessibility Statement
          </a>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
const zoomPlus = `<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor"  viewBox="0 0 512 512"><path d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376C296.3 401.1 253.9 416 208 416 93.1 416 0 322.9 0 208S93.1 0 208 0 416 93.1 416 208zM208 112c-13.3 0-24 10.7-24 24l0 48-48 0c-13.3 0-24 10.7-24 24s10.7 24 24 24l48 0 0 48c0 13.3 10.7 24 24 24s24-10.7 24-24l0-48 48 0c13.3 0 24-10.7 24-24s-10.7-24-24-24l-48 0 0-48c0-13.3-10.7-24-24-24z"/></svg>`;
const contrast = `<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor"  viewBox="0 0 512 512"><path d="M448 256c0-106-86-192-192-192l0 384c106 0 192-86 192-192zM0 256a256 256 0 1 1 512 0 256 256 0 1 1 -512 0z"/></svg>`;
const font = `<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 512 512"><path d="M285.1 50.7C279.9 39.3 268.5 32 256 32s-23.9 7.3-29.1 18.7L59.5 416 48 416c-17.7 0-32 14.3-32 32s14.3 32 32 32l88 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-6.1 0 22-48 208.3 0 22 48-6.1 0c-17.7 0-32 14.3-32 32s14.3 32 32 32l88 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-11.5 0-167.4-365.3zM330.8 304L181.2 304 256 140.8 330.8 304z"/></svg>`;
const pause = `<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor"  viewBox="0 0 512 512"><path d="M256 512a256 256 0 1 0 0-512 256 256 0 1 0 0 512zM224 192l0 128c0 17.7-14.3 32-32 32s-32-14.3-32-32l0-128c0-17.7 14.3-32 32-32s32 14.3 32 32zm128 0l0 128c0 17.7-14.3 32-32 32s-32-14.3-32-32l0-128c0-17.7 14.3-32 32-32s32 14.3 32 32z"/></svg>`;
const focus = `<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 640 512"><path d="M176 56c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 38.1-63-63c-9.4-9.4-24.6-9.4-33.9 0S21.7 55.6 31 65l63 63-38.1 0c-13.3 0-24 10.7-24 24s10.7 24 24 24l96 0c13.3 0 24-10.7 24-24l0-96zm0 400l0-96c0-13.3-10.7-24-24-24l-96 0c-13.3 0-24 10.7-24 24s10.7 24 24 24l38.1 0-63 63c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l63-63 0 38.1c0 13.3 10.7 24 24 24s24-10.7 24-24zm312 24c13.3 0 24-10.7 24-24l0-38.1 63 63c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-63-63 38.1 0c13.3 0 24-10.7 24-24s-10.7-24-24-24l-96 0c-13.3 0-24 10.7-24 24l0 96c0 13.3 10.7 24 24 24zM464 56l0 96c0 13.3 10.7 24 24 24l96 0c13.3 0 24-10.7 24-24s-10.7-24-24-24l-38.1 0 63-63c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-63 63 0-38.1c0-13.3-10.7-24-24-24s-24 10.7-24 24zM320 120c-57.3 0-99.4 34-125.6 64.3-20 23.1-32.4 45.8-37.6 56.1-2.5 5-4.9 9.7-4.9 15.6s2.3 10.6 4.9 15.6c5.2 10.3 17.6 33 37.6 56.1 26.2 30.3 68.2 64.3 125.6 64.3s99.4-34 125.6-64.3c20-23.1 32.4-45.8 37.6-56.1 2.5-5 4.9-9.7 4.9-15.6s-2.3-10.6-4.9-15.6c-5.2-10.3-17.6-33-37.6-56.1-26.2-30.3-68.2-64.3-125.6-64.3zM256 256a64 64 0 1 1 128 0 64 64 0 1 1 -128 0z"/></svg>`;

const STORAGE_KEY = "acc-settings";
const CONTRAST_LAYER_ID = "acc-contrast-layer";

const defaultSettings = () => ({
  fontsize: 0, // 0,1,2
  contrast: 0, // 0,1,2
  dyslexia: false,
  motion: false,
  focus: false,
});

export default {
  name: "Accessibility",
  props: {
    button: {
      type: String,
      default: "Accessibility",
    },
    buttonClass: {
      type: String,
      default: "",
    },
    statementUrl: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      open: false,

      settings: defaultSettings(),

      options: [
        {label: "Increase Text Size", value: "fontsize", icon: zoomPlus},
        {label: "High Contrast Mode", value: "contrast", icon: contrast},
        {label: "Dyslexia-Friendly Font", value: "dyslexia", icon: font},
        {label: "Reduce Animations", value: "motion", icon: pause},
        {label: "Highlight Keyboard Focus", value: "focus", icon: focus},
      ],
    };
  },

  mounted() {
    const hasSaved = this.loadSettings();

    // fall back to the browser/OS preferences when nothing was saved yet
    if (!hasSaved) {
      if (window.matchMedia("(prefers-contrast: more)").matches) {
        this.settings.contrast = 2; // 150%
      } else if (window.matchMedia("(prefers-contrast: less)").matches) {
        this.settings.contrast = 1; // 50%
      }

      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        this.settings.motion = true;
      }
    }

    this.applyAll();
    document.addEventListener("keydown", this.onKeydown);
  },

  beforeUnmount() {
    document.removeEventListener("keydown", this.onKeydown);
  },

  methods: {
    toggleModal() {
      if (this.open) this.closeModal();
      else this.openModal();
    },

    openModal() {
      this.open = true;
      document.documentElement.style.overflow = "hidden";
    },

    closeModal() {
      this.open = false;
      document.documentElement.style.overflow = "";
    },

    onKeydown(event) {
      if (event.key === "Escape" && this.open) this.closeModal();
    },

    isActive(value) {
      if (value === "fontsize") return this.settings.fontsize > 0;
      if (value === "contrast") return this.settings.contrast > 0;
      return this.settings[value];
    },

    toggleOption(option) {
      switch (option) {
        case "fontsize":
          this.settings.fontsize = (this.settings.fontsize + 1) % 3;
          break;

        case "contrast":
          this.settings.contrast = (this.settings.contrast + 1) % 3;
          break;

        case "dyslexia":
        case "motion":
        case "focus":
          this.settings[option] = !this.settings[option];
          break;
      }

      this.saveSettings();
      this.applyAll();
    },

    saveSettings() {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.settings));
    },

    loadSettings() {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (!saved) return false;

        this.settings = {...defaultSettings(), ...JSON.parse(saved)};
        return true;
      } catch {
        return false;
      }
    },

    applyAll() {
      const html = document.documentElement;

      // TEXT SIZE
      html.classList.remove("text-size-1", "text-size-2");
      if (this.settings.fontsize === 1) html.classList.add("text-size-1");
      if (this.settings.fontsize === 2) html.classList.add("text-size-2");

      // CONTRAST
      html.classList.remove("contrast-50", "contrast-150");
      if (this.settings.contrast === 1) html.classList.add("contrast-50");
      if (this.settings.contrast === 2) html.classList.add("contrast-150");
      this.applyContrastLayer();

      html.classList.toggle("dyslexia-font", this.settings.dyslexia);
      html.classList.toggle("reduce-motion", this.settings.motion);
      html.classList.toggle("focus-highlight", this.settings.focus);
    },

    /**
     * Contrast is applied through a fixed layer using `backdrop-filter`
     * instead of a `filter` on <html>. A filter on <html> would turn it into
     * the containing block for every `position: fixed` element on the page
     * (this drawer included), which anchors them to the top of the document
     * instead of the viewport and makes them scroll out of sight.
     *
     * The layer is a singleton: the footer renders the component twice
     * (desktop + mobile) and two stacked layers would filter twice.
     */
    applyContrastLayer() {
      let layer = document.getElementById(CONTRAST_LAYER_ID);

      if (!this.settings.contrast) {
        if (layer) layer.remove();
        return;
      }

      if (!layer) {
        layer = document.createElement("div");
        layer.id = CONTRAST_LAYER_ID;
        layer.setAttribute("aria-hidden", "true");
        document.body.appendChild(layer);
      }

      layer.className = `acc-contrast-layer acc-contrast-${this.settings.contrast}`;
    },

    resetSettings() {
      this.settings = defaultSettings();
      localStorage.removeItem(STORAGE_KEY);
      this.applyAll();
    },
  },
};
</script>

<style>
:root {
  /* ark palette: blue / green */
  --acc-color: #0057c2;
  --acc-active-color: #92d500;
}

/* TEXT SIZE */
html.text-size-1 {
  font-size: 110% !important;
}
html.text-size-2 {
  font-size: 125% !important;
}

/* HIGH CONTRAST
   Filters the backdrop from a fixed layer instead of putting a `filter` on
   <html>, which would break every `position: fixed` element on the page.
   It sits below the drawer so the settings panel itself stays readable. */
.acc-contrast-layer {
  position: fixed;
  inset: 0;
  z-index: 9999998;
  pointer-events: none;
}

.acc-contrast-1 {
  -webkit-backdrop-filter: contrast(50%);
  backdrop-filter: contrast(50%);
}

.acc-contrast-2 {
  -webkit-backdrop-filter: contrast(150%);
  backdrop-filter: contrast(150%);
}

/* DYSLEXIA-FRIENDLY FONT */
html.dyslexia-font,
.dyslexia-font body,
.dyslexia-font * {
  font-family: "Dyslexic", sans-serif !important;
}

/* REDUCE ANIMATIONS */
html.reduce-motion * {
  animation: none !important;
  transition: none !important;
  scroll-behavior: auto !important;
}

/* FOCUS HIGHLIGHT */
html.focus-highlight *:focus {
  outline: 3px solid var(--acc-color) !important;
  outline-offset: 3px !important;
}

/* OVERLAY */
.acc-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(2px);
  z-index: 9999999;
}

/* DRAWER */
.acc-drawer {
  position: fixed;
  width: 550px;
  max-width: 90%;
  height: 100vh;
  background: #fff;
  z-index: 99999999;
  top: 0;
  right: -100%;
  visibility: hidden;
  transition:
    right 0.35s ease,
    visibility 0.35s ease;
  overflow-y: auto;
  padding: 30px;
  color: var(--acc-color);
}

@media (max-width: 639px) {
  .acc-drawer {
    width: 100%;
    max-width: 100%;
    padding: 20px;
  }
}

.acc-drawer.open {
  right: 0;
  visibility: visible;
}

.acc-title {
  color: var(--acc-color);
}

.acc-option-icon {
  width: 24px;
  display: block;
}

.acc-option-btn {
  width: 100%;
  padding: 14px 16px;
  background: #fff;
  border: 1px solid var(--acc-color);
  border-radius: 30px;
  text-align: left;
  font-size: inherit;
  color: var(--acc-color);
  font-weight: 500;
  transition: all 0.25s ease;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.acc-option-btn.active {
  border-color: var(--acc-active-color);
  background: var(--acc-active-color);
  color: var(--acc-color);
}

.acc-option-btn:active {
  transform: scale(0.99);
}
</style>
